import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import round from 'lodash/round';

import { SRCSET_WIDTHS, isSvg, resolveImageUrl, buildImgixUrl, candidateWidths, pickFallbackWidth } from 'Helpers/Image/image-urls';
import useViewport from 'Hooks/useViewport';
import useSize from 'Hooks/useSize';


// The maximum width for the fallback `src` (the `src` attribute) is 400px.
// It prevents the browser from downloading unnecessarily large images when the layout is fluid.
const MAX_FALLBACK_WIDTH = 400;

// Convert a measured width in px to a vw percentage of the viewport width.
// If the viewport width is 0 (e.g. during server-side rendering) return a default of 33vw.
const measuredVw = (width, windowWidth) => {
  const vw = round((width / windowWidth) * 100);
  return (!vw || vw > 100) ? 33 : vw;
};

// Build the image URL from the relative `src` and the CDN `folder`/`subfolder`.
// If a `youtubeId` is provided, use the YouTube thumbnail instead of the relative `src`.
const sourceUrl = ({ src, folder, subfolder, youtubeId }) => (youtubeId ? `https://ytimg.imgix.net/${youtubeId}` : resolveImageUrl({ src, folder, subfolder }));

// Return the effective aspect ratio to use for cropping. If `aspectRatio` is provided, use it; otherwise, if `ratio16by9` is true, use 16:9; otherwise, return 0.
const effectiveRatio = (aspectRatio, ratio16by9) => aspectRatio || (ratio16by9 ? 16 / 9 : 0);

// Remove any widths that are near-duplicates of the ladder srcset widths.
// A width is considered a near-duplicate if it is within 15% of a width.
const nearAny = (ladderWidth, widths) =>
  widths.some(width => (width / ladderWidth) > 0.85 && (width / ladderWidth) < 1.15);

// Return the candidate widths for the srcSet, based on the ladder widths and any additional widths from the `sizes` override. The returned widths are de-duplicated and ordered largest-first.
const widthsFromSizes = (sizesOverride, windowWidth) => {
  const parts = sizesOverride.split(',').map(part => part.trim());

  const pxWidths = parts
    .map(part => part.match(/(\d+)px$/))
    .filter(Boolean)
    .map(match => Number(match[1]));

  const vwWidths = parts
    .map(part => part.match(/(\d+)vw$/))
    .filter(Boolean)
    .map(match => round((Number(match[1]) / 100) * windowWidth));

  const widths = [...pxWidths, ...vwWidths];

  return {
    widths: [...widths, ...widths.map(width => width * 2)],
    maxFallback: pxWidths.length ? Math.max(...pxWidths) : MAX_FALLBACK_WIDTH,
  };
};

/**
 * The srcSet candidate widths plus the fallback-`src` ceiling. With a `sizes`
 * override the widths come from it (plus any ladder widths that aren't near-
 * duplicates); otherwise from the full ladder plus the width the image actually
 * renders at.
 *
 * @returns {{ widths: number[], maxFallback: number }}
 */
const srcSetWidths = ({ sizesOverride, renderedVw, windowWidth }) => {
  if (sizesOverride) {
    const { widths, maxFallback } = widthsFromSizes(sizesOverride, windowWidth);
    return { widths: [...widths, ...SRCSET_WIDTHS.filter(w => !nearAny(w, widths))], maxFallback };
  }

  return {
    widths: candidateWidths(round((renderedVw / 100) * windowWidth)),
    maxFallback: MAX_FALLBACK_WIDTH,
  };
};


/**
 * FluidImg — a responsive <img> that fills a fluid layout slot.
 *
 * Which image component do I use?
 *   • FluidImg           — a responsive image sized to its container (this one).
 *   • FixedImg           — a fixed-size image: logos, icons, badges.
 *   • BackgroundImg      — a background image with content layered on top.
 *
 * Give it a relative `src` plus the CDN `folder`/`subfolder`. It measures how
 * wide it renders (or takes a `sizesOverride`), builds an imgix srcSet across a
 * ladder of widths, and lets the browser pick the best source.
 *
 * @param {Object}  size          - Measured { width }; supplied internally by the wrapper.
 * @param {string}  src           - Image file name / relative path.
 * @param {string}  alt           - Alt text.
 * @param {string}  className     - Extra CSS classes.
 * @param {string}  folder        - CDN folder (default 'product').
 * @param {string}  subfolder     - CDN subfolder.
 * @param {string}  overrideWidth - Fixed vw width to use instead of measuring.
 * @param {string}  sizesOverride - Explicit `sizes` value for eager/known layouts.
 * @param {number}  aspectRatio   - Crop sources to this width/height ratio (e.g. 1 for square).
 * @param {boolean} ratio16by9    - Shorthand for aspectRatio={16 / 9}; ignored if aspectRatio is set.
 * @param {boolean} fullQuality   - Request higher quality (q=95) for large widths.
 * @param {number}  dpr           - Device pixel ratio to bake in (only emitted when > 1).
 * @param {boolean} lazy          - Lazy-load the image (default true).
 * @param {string}  youtubeId     - Use a YouTube thumbnail as the source instead of `src`.
 */
const FluidImg = forwardRef(({
  size,
  src,
  alt,
  className,
  folder,
  subfolder,
  overrideWidth,
  sizesOverride,
  aspectRatio,
  ratio16by9,
  fullQuality,
  dpr,
  lazy,
  youtubeId,
}, ref) => {
  const { width: windowWidth } = useViewport();

  // SVGs are vector — serve untouched, no imgix, no srcSet.
  if (isSvg(src)) {
    return (
      <img
        ref={ref}
        src={resolveImageUrl({ src, folder, subfolder })}
        alt={alt}
        className={`img-fluid img-full ${className}`}
        loading={lazy ? 'lazy' : 'eager'}
        fetchpriority={lazy ? undefined : 'high'}
      />
    );
  }

  // The width the image renders at, in vw. If `overrideWidth` is provided, use it instead of measuring.
  const renderedVw = overrideWidth || measuredVw(size.width, windowWidth);
  // The base URL for the image, without any imgix query string.
  const base = sourceUrl({ src, folder, subfolder, youtubeId });
  // The candidate widths for the srcSet, plus the fallback-`src` ceiling.
  const { widths, maxFallback } = srcSetWidths({ sizesOverride, renderedVw, windowWidth });
  // The effective aspect ratio to use for cropping, or 0 if no crop.
  const ratio = effectiveRatio(aspectRatio, ratio16by9);

  // Build the imgix URL for a given width, with optional height if cropping to a ratio.
  // If `fullQuality` is true and the width is > 950px, request q=95; otherwise use the default quality.
  const toUrl = w => buildImgixUrl(base, {
    width: w,
    dpr,
    quality: fullQuality && w > 950 ? 95 : undefined,
    ...(ratio ? { fit: 'crop', height: round(w / ratio) } : { fit: 'max' }),
  });

  // Sorted widths for the srcSet, largest-first. The fallback `src` is the largest width that is <= maxFallback.
  const sortedWidths = [...widths].sort((a, b) => b - a);

  return (
    <img
      ref={ref}
      srcSet={sortedWidths.map(w => `${toUrl(w)} ${w}w`).join(', ')}
      sizes={sizesOverride || `${renderedVw}vw`}
      src={toUrl(pickFallbackWidth(sortedWidths, maxFallback))}
      alt={alt}
      loading={lazy ? 'lazy' : 'eager'}
      fetchpriority={lazy ? 'low' : 'high'}
      decoding={lazy ? 'async' : undefined}
      className={`img-fluid img-full ${className}`}
    />
  );
});

FluidImg.propTypes = {
  size: PropTypes.shape({}),
  src: PropTypes.string,
  ratio16by9: PropTypes.bool,
  fullQuality: PropTypes.bool,
  alt: PropTypes.string,
  className: PropTypes.string,
  subfolder: PropTypes.string,
  folder: PropTypes.string,
  overrideWidth: PropTypes.string,
  sizesOverride: PropTypes.string,
  aspectRatio: PropTypes.number,
  youtubeId: PropTypes.string,
  lazy: PropTypes.bool,
  dpr: PropTypes.number,
};

FluidImg.defaultProps = {
  size: {},
  src: '',
  alt: '',
  className: '',
  ratio16by9: false,
  fullQuality: false,
  folder: 'product',
  subfolder: '',
  overrideWidth: '',
  sizesOverride: '',
  aspectRatio: 0,
  youtubeId: '',
  dpr: 1,
  lazy: true,
};

/**
 * Wrapper that measures the rendered <img> and feeds its width back in as `size`.
 * With an `overrideWidth` or `sizesOverride` there is nothing to measure, so we
 * skip the ref and render the image directly.
 */
function FluidImgWrapper(props) {
  const [ref, size] = useSize();

  if (props.overrideWidth || props.sizesOverride) {
    return (<FluidImg {...props} />);
  }

  return (<FluidImg ref={ref} size={size} {...props} />);
}

FluidImgWrapper.propTypes = {
  overrideWidth: PropTypes.string,
  sizesOverride: PropTypes.string,
};

FluidImgWrapper.defaultProps = {
  overrideWidth: '',
  sizesOverride: '',
};

export default FluidImgWrapper;
